// sometimes a layer isn't found right away if it was just created
function getElement(selector, { timeout = 2000, start = Date.now() } = {}) {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      if (typeof(selector) == 'string' && selector.startsWith('#')) {
        selector = `${selector}, a[name="${selector.substring(1)}"]`;
      }
      const element = typeof(selector) == 'string' ? document.querySelector(selector) : selector;
      if (element) {
        resolve(element);
      } else if (Date.now() - start < timeout) {
        getElement(selector, { timeout, start }).then(resolve);
      } else {
        resolve(null);
      }
    });
  });
}

function getElementHeight(element) {
  if (element === undefined || element === null) {
    return 0;
  } else if (typeof element == 'number') {
    return element;
  } else {
    const e =
      typeof element == 'string' ? document.querySelector(element) : element;
    if (e) {
      return e.clientHeight;
    } else {
      return 0;
    }
  }
}

function relativeScrollAmount(val) {
  if (val < 0) {
    return `-=${Math.abs(val)}px`;
  } else if (val > 0) {
    return `+=${val}px`;
  } else {
    return 0;
  }
}

function getScrollAmount(current, val = null) {
  let amount = current;
  if (val !== null) {
    switch (typeof val) {
      case 'string':
        if (val.indexOf('+=') == 0) {
          amount += parseInt(val.substring(2), 10);
        } else if (val.indexOf('-=') == 0) {
          amount -= parseInt(val.substring(2), 10);
        } else {
          amount = parseInt(val, 10);
        }
        break;
      case 'number':
        amount = val;
        break;
      default:
        amount = 0;
    }
    if (amount < 0) {
      amount = 0;
    }
  }
  return amount;
}

function watchScroll(container, x, y, cb, lastX, lastY, force) {
  // sometimes the scroll amount is not a whole number
  const currentX = Math.round(container.scrollLeft);
  const currentY = Math.round(container.scrollTop);
  if (currentX === lastX && currentY === lastY && force === true) {
    // if scrolling has stopped execute the callback
    cb();
  } else if (
    currentX >= x - 1 &&
    currentX <= x + 1 &&
    currentY >= y - 1 &&
    currentY <= y + 1
  ) {
    // since we rounded make sure we're within 1px
    requestAnimationFrame(cb);
  } else {
    requestAnimationFrame(() => {
      watchScroll(
        container,
        x,
        y,
        cb,
        currentX,
        currentY,
        currentX === lastX && currentY === lastY
      );
    });
  }
}

function offsetTop(element) {
  return element.getBoundingClientRect().top + window.scrollY;
}

export const waitReadyState = (() => {
  const readyStates = { loading: 0, interactive: 1, complete: 2 };
  return (readyState) => {
    return new Promise((resolve) => {
      const check = () => {
        if (readyStates[document.readyState] >= readyStates[readyState]) {
          document.removeEventListener('readystatechange', check);
          resolve();
        } else {
          document.addEventListener('readystatechange', check, {
            passive: true,
          });
        }
      };
      check();
    });
  };
})();

/**
 * Scroll to an x/y coordinate
 * @param {object} [opts]
 * @param {number|string} [opts.x] The X coordinate
 * @param {number|string} [opts.y] The Y coordinate
 * @param {boolean} [opts.smooth=true] Use smooth scrolling if available
 * @param {HTMLElement} [opts.container=`document.scrollingElement`] The element being scrolled
 * @returns {Promise<void>}
 */
export function scroll({
  x = null,
  y = null,
  smooth = true,
  container = document.scrollingElement,
} = {}) {
  return new Promise((resolve) => {
    const coords = {
      x: getScrollAmount(container.scrollLeft, x),
      y: getScrollAmount(container.scrollTop, y),
    };
    if (
      !container ||
      container === document.documentElement ||
      container === document.body ||
      container === window
    ) {
      // this seems to be generally safer
      container = document.scrollingElement || window;
    }
    if (smooth) {
      try {
        container.scroll({ left: coords.x, top: coords.y, behavior: 'smooth' });
      } catch {
        container.scroll(coords.x, coords.y);
      }
    } else {
      container.scroll(coords.x, coords.y);
    }
    watchScroll(container, coords.x, coords.y, () => {
      resolve();
    });
  });
}

/**
 * Scroll to the top
 * @param {object} [opts]
 * @param {boolean} [opts.smooth=true] Use smooth scrolling if available
 * @param {HTMLElement} [opts.container=`document.scrollingElement`] The element being scrolled
 * @returns {Promise<void>}
 */
export function scrollToTop({
  smooth = true,
  container = document.scrollingElement,
} = {}) {
  return scroll({ y: 0, container, smooth });
}

/**
 * Scroll to the bottom
 * @param {object} [opts]
 * @param {boolean} [opts.smooth=true] Use smooth scrolling if available
 * @param {HTMLElement} [opts.container=`document.scrollingElement`] The element being scrolled
 * @returns {Promise<void>}
 */
export function scrollToBottom({
  smooth = true,
  container = document.scrollingElement,
} = {}) {
  return scroll({
    y: container.offsetHeight,
    container,
    smooth,
  });
}

/**
 * Scroll to an element
 * @param {object|string} selector
 * @param {object} [opts]
 * @param {boolean} [opts.center=false] Center within the viewport
 * @param {number|object|string} [opts.header=0] Account for a static header. Can be a number, `HTMLElement` or a string selector.
 * @param {number} [opts.extra=0] An extra amount to scroll
 * @param {boolean} [opts.smooth=true] Use smooth scrolling if available
 * @param {HTMLElement} [opts.container=`document.scrollingElement`] The element being scrolled
 * @returns {Promise<void>}
 */
export function scrollTo(
  selector,
  {
    center = false,
    header = 0,
    extra = 0,
    smooth = true,
    container = document.scrollingElement,
  } = {}
) {
  return new Promise((resolve) => {
    getElement(selector).then((element) => {
      if (element) {
        header = getElementHeight(header);
        let y = offsetTop(element);
        if (center) {
          const containerHeight = container.clientHeight;
          const elementHeight = element.clientHeight;
          if (elementHeight < containerHeight) {
            y -= (containerHeight - elementHeight) / 2 - extra;
          }
        } else {
          y -= header - extra;
        }
        // if (y !== 0) {
        scroll({ y: y, container, smooth }).then(() => {
          resolve();
        });
        // } else {
        //   resolve();
        // }
      } else {
        resolve();
      }
    });
  });
}

/**
 * Scroll an element into view
 * @param {object|string} selector
 * @param {object} [opts]
 * @param {number|object|string} [opts.header=0] Account for a static header. Can be a number, `HTMLElement` or a string selector.
 * @param {number|object|string} [opts.footer=0] Account for a static footer. Can be a number, `HTMLElement` or a string selector.
 * @param {number} [opts.extra=0] An extra amount to scroll
 * @param {boolean} [opts.smooth=true] Use smooth scrolling if available
 * @param {HTMLElement} [opts.container=`document.scrollingElement`] The element being scrolled
 * @returns {Promise<void>}
 */
export function scrollIntoView(
  selector,
  {
    header = 0,
    footer = 0,
    extra = 0,
    smooth = true,
    container = document.scrollingElement,
  } = {}
) {
  return new Promise((resolve) => {
    getElement(selector).then((element) => {
      if (element) {
        header = getElementHeight(header);
        footer = getElementHeight(footer);

        let y = null;

        const containerScrollTop = container.scrollTop;
        const containerHeight = container.clientHeight;
        const elementOffsetTop = offsetTop(element);
        const elementHeight = element.clientHeight;
        if (elementHeight > containerHeight) {
          y = elementOffsetTop - header - extra;
        } else if (
          elementOffsetTop + elementHeight >
          containerHeight + containerScrollTop - footer
        ) {
          y = relativeScrollAmount(
            elementOffsetTop +
              elementHeight -
              containerHeight -
              containerScrollTop +
              footer +
              extra
          );
        } else if (elementOffsetTop < containerScrollTop + header) {
          y = relativeScrollAmount(
            elementOffsetTop - containerScrollTop - header - extra
          );
        }

        if (y !== null) {
          scroll({ y: y, container, smooth }).then(() => {
            resolve();
          });
        } else {
          resolve();
        }
      } else {
        resolve();
      }
    });
  });
}

/**
 * If `location` has an anchor, attempt to locate an element and scroll to it. An anchor value of `#[id]` will look for an element with the ID `[id]`. A value of `#.[selector]` will look for the first element that matches the selector `.[selector]`.
 * @param {object} [opts]
 * @param {number|object|string} [opts.header=0] Account for a static header. Can be a number, `HTMLElement` or a string selector.
 * @param {number|object|string} [opts.footer=0] Account for a static footer. Can be a number, `HTMLElement` or a string selector.
 * @param {number} [opts.extra=0] An extra amount to scroll
 * @param {boolean} [opts.smooth=true] Use smooth scrolling if available
 * @param {string|Promise<void>} [opts.on=`load`] The event or Promise that should trigger scrolling.
 * @returns {Promise<void>}
 */
export function scrollToLocationAnchor({
  header = 0,
  footer = 0,
  extra = 0,
  smooth = true,
  on,
} = {}) {
  return new Promise((resolve) => {
    if (location.hash) {
      (() => {
        if (on instanceof Promise) {
          return on;
        } else {
          switch (on) {
            case 'ready':
              return waitReadyState('interactive');
            case 'interactive':
            case 'complete':
              return waitReadyState(on);
            case 'load':
            case 'loaded':
            default:
              if (document.readyState === 'complete') {
                return Promise.resolve();
              } else {
                return new Promise((resolve) => {
                  window.addEventListener('load', resolve, { passive: true });
                });
              }
          }
        }
      })().then(() => {
        return getElement(location.hash)
      }).then((element) => {
        if (element) {
          scrollTo(element, { header, footer, extra, smooth });
        }
      });
    } else {
      resolve();
    }
  });
}


// ********************************************************************************

function calculateScrollExtra(element) {
  let extra = -10;
  if (element.tagName !== 'SECTION') {
    extra = (window.innerWidth < 992 ? -60 : -83);
    if (element) {
      const cs = getComputedStyle(element);
      extra += parseInt(cs['padding-top']);
      extra += parseInt(cs['margin-top']);
    }
  }
  return extra;
}

export default function scrolling() {

  if (location.hash) {
    getElement(location.hash).then((element) => {
      if (element) {
        if (!element.closest('.tab-accordion-scope .nav-tabs')) {
            scrollToLocationAnchor({ extra: calculateScrollExtra(element) });
        }
        scrollToLocationAnchor({ extra: calculateScrollExtra(element) });
      }
    });
  }

	waitReadyState('interactive')
    .then(() => {
      document.querySelectorAll('a[href^="#"]').forEach((a) => {
        if (a.getAttribute('href') !== '#') {
          const isTabLink = a.classList.contains('nav-link'); // Check if it's a Bootstrap tab/nav link
          const isAccordionToggle = a.classList.contains('btn-link') && a.closest('.accordion'); // Check if it's an accordion toggle

          // If the link is a tab or accordion toggle, do NOT prevent default and do NOT scroll
          if (isTabLink || isAccordionToggle) {
            return; // Skip this anchor, let Bootstrap's JS handle it
          }
          a.addEventListener('click', (e) => {
            e.preventDefault();
            const selector = a.getAttribute('href');
            getElement(selector).then((element) => {
              scrollTo(element, { extra: calculateScrollExtra(element) });
              history.pushState({}, '', `${location.pathname}${selector}`);
            });
          });
        }
      });
    });
}